import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { JsonObjectFactoryService } from '../../../global/services/json-object-factory.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  buildHeaderDataShell,
  extractObject,
  firstGridRowObjectFromPayload,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { RelatedPartyModalService } from './related-party-modal.service';

interface RelatedPartyRowKeys {
  LEID: any;
  CDR_NO: any;
  REPORTING_PERIOD: any;
  ME_CODE: any;
  CURR: any;
  PAYOR_LEID: any;
  PAYOR_CDR_NO: any;
  PAYOR_REPORTING_PERIOD: any;
  PAYOR_ME_CODE: any;
  PAYOR_CURR: any;
}

@Component({
  selector: 'gtw-related-party-modal',
  templateUrl: './related-party-modal.component.html',
  styleUrls: ['./related-party-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RelatedPartyModalComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  private initContextWarningShown = false;

  modalTitle = 'Explain Variance';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  loadFailed = false;

  tabs = [
    { id: 'explainVariance', name: 'Explain Variance' },
    { id: 'setupExplanation', name: 'Setup New Standard Explanation' },
  ];
  activeTab = 'explainVariance';

  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  globalParams: any = {};
  filterParams: any = {};
  rowData: any = {};

  explainText = '';
  curExplDesc = '';
  otherExplainText = '';
  varianceExpln: any = null;
  showOtherExpln = false;
  explDataLoaded = false;
  showRemoveBtn = false;

  explDropdownData: any[] = [];

  showConfirmDialog = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Remove Explanation',
    message: 'Are you sure you want to remove this Explanation?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingConfirmAction: (() => void) | null = null;

  constructor(
    private service: RelatedPartyModalService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      }),
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.globalParams = header.globalParams || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
    this.cdr.markForCheck();
  }

  private hasRequiredRowContext(row: any): boolean {
    const payload = extractObject(row);
    return !!(
      payload &&
      payload.LEID !== undefined &&
      payload.CDR_NO !== undefined &&
      payload.REPORTING_PERIOD !== undefined &&
      payload.ME_CODE !== undefined &&
      payload.CURR !== undefined
    );
  }

  private blockInit(reason: string): void {
    // Do NOT touch UI state here — the loader stays visible while we wait for
    // the workspace context to become available (rowData typically arrives via
    // CeHostDialog's `bridgeDataToActiveScreen` shortly after first CD pass).
    // The empty/error warning is reserved for confirmed API failures.
    if (!this.initContextWarningShown) {
      this.initContextWarningShown = true;
      console.warn('[RelatedPartyModal] Initialization skipped:', reason, {
        rowDataKeys: Object.keys(this.rowData || {}),
      });
    }
  }

  private tryInit(): void {
    if (this.isInitialized) return;

    if (!this.GENERAL_CONFIG?.base_url) {
      this.blockInit('GENERAL_CONFIG.base_url is missing');
      return;
    }
    if (!this.hasRequiredRowContext(this.rowData)) {
      this.blockInit('row-data is empty or missing required keys');
      return;
    }

    this.initContextWarningShown = false;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.explDataLoaded = false;
    this.loadFailed = false;
    this.getVarExplanation();
  }

  private buildRowKeysPayload(): RelatedPartyRowKeys {
    return {
      LEID: this.rowData.LEID,
      CDR_NO: this.rowData.CDR_NO,
      REPORTING_PERIOD: this.rowData.REPORTING_PERIOD,
      ME_CODE: this.rowData.ME_CODE,
      CURR: this.rowData.CURR,
      PAYOR_LEID: this.rowData.PAYOR_LEID,
      PAYOR_CDR_NO: this.rowData.PAYOR_CDR_NO,
      PAYOR_REPORTING_PERIOD: this.rowData.PAYOR_REPORTING_PERIOD,
      PAYOR_ME_CODE: this.rowData.PAYOR_ME_CODE,
      PAYOR_CURR: this.rowData.PAYOR_CURR,
    };
  }

  /** Get current variance explanation for the row (action code: aca72h). */
  private getVarExplanation(): void {
    if (!this.hasRequiredRowContext(this.rowData)) {
      this.blockInit('rowData became invalid before getVarExplanation');
      return;
    }

    const params = {
      action_code: 'aca72h',
      json_data: JSON.stringify([this.buildRowKeysPayload()]),
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject', params,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        const items: any[] = response?.jsonObject || [];
        if (items.length > 0) {
          if (items[0].EXPLN_TYPE === 'OTH') {
            this.curExplDesc = 'Other';
            this.otherExplainText = items[0].EXPLN_TEXT;
            this.showOtherExpln = true;
          } else {
            this.curExplDesc = items[0].EXPLN_TEXT;
            this.showOtherExpln = false;
          }
        }
        this.getExplanations();
      },
      (error: any) => {
        console.error('Error loading variance explanation:', error);
        this.showErrorSnackbar('Error loading variance explanation');
        this.getExplanations();
      },
    );
  }

  /** Get all standard explanations for dropdown (action code: tg9rwv). */
  private getExplanations(): void {
    this.varianceExpln = null;
    this.showRemoveBtn = false;

    const params = { action_code: 'tg9rwv' };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject', params,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        const items: any[] = response?.jsonObject || [];
        this.explDropdownData = items;

        if (items.length === 0) {
          this.explDataLoaded = false;
          this.loadFailed = true;
          this.cdr.detectChanges();
          return;
        }

        this.explDataLoaded = true;
        this.loadFailed = false;

        if (this.curExplDesc !== '') {
          this.showRemoveBtn = true;
          for (const obj of this.explDropdownData) {
            if (this.curExplDesc === obj.EXPL_DESC) {
              this.varianceExpln = obj;
              break;
            }
          }
        }

        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading explanations:', error);
        this.showErrorSnackbar('Error loading explanations');
        this.explDataLoaded = false;
        this.loadFailed = true;
        this.cdr.detectChanges();
      },
    );
  }

  onVarianceExplnChange(): void {
    const selected = this.varianceExpln;
    this.showOtherExpln = selected?.EXPL_DESC === 'Other';
    if (!this.showOtherExpln) {
      this.otherExplainText = '';
    }
    this.cdr.detectChanges();
  }

  /** Save new explanation (Setup New Standard Explanation tab, action code: b67t0o). */
  saveNewExplanation(): void {
    if (this.explainText === '') {
      this.showErrorSnackbar('Please enter a valid explanation');
      return;
    }
    if (this.explainText.length > 500) {
      this.showErrorSnackbar('Explanation is too long. Please shorten to 500 characters or less.');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.explDataLoaded = false;
    this.loadFailed = false;
    this.showOtherExpln = false;
    this.cdr.detectChanges();

    const ssoId = this.USER_SETTINGS?.user?.sso_id || this.globalParams?.sso_id;
    const jsonSettings = { sso_id: ssoId };

    this.service.saveNewExplanation(
      this.GENERAL_CONFIG.base_url,
      [],
      jsonSettings,
      this.explainText,
      this.globalParams,
      this.filterParams,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this.showErrorSnackbar(response.errorMessage);
          this.explDataLoaded = this.explDropdownData.length > 0;
          this.loadFailed = this.explDropdownData.length === 0;
        } else {
          this.showSuccessSnackbar('New explanation has been saved');
          this.getVarExplanation();
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.explDataLoaded = this.explDropdownData.length > 0;
        this.loadFailed = this.explDropdownData.length === 0;
        console.error('Error saving new explanation:', error);
        this.showErrorSnackbar('Error occurred while saving. Please try again.');
        this.cdr.detectChanges();
      },
    );
  }

  /** Save variance explanation (Explain Variance tab, action code: otidof). */
  saveVarianceExpln(): void {
    if (!this.varianceExpln?.EXPL_DESC) {
      this.showErrorSnackbar('Please select an explanation.');
      return;
    }

    let explnText = '';
    let explnType = 'STD';

    if (this.varianceExpln.EXPL_DESC === 'Other') {
      if (this.otherExplainText === '') {
        this.showErrorSnackbar('Please enter other explanation');
        return;
      }
      if (this.otherExplainText.length > 500) {
        this.showErrorSnackbar('Other explanation is too long. Please shorten to 500 characters or less.');
        return;
      }
      explnText = this.otherExplainText;
      explnType = 'OTH';
    } else {
      explnText = this.varianceExpln.EXPL_DESC;
      explnType = 'STD';
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const ssoId = this.USER_SETTINGS?.user?.sso_id || this.globalParams?.sso_id;
    const jsonSettings = { sso_id: ssoId };

    this.service.saveVarExplanation(
      this.GENERAL_CONFIG.base_url,
      [this.buildRowKeysPayload()],
      jsonSettings,
      explnText,
      explnType,
      this.globalParams,
      this.filterParams,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this.showErrorSnackbar(response.errorMessage);
        } else {
          this.showSuccessSnackbar('Variance explanation has been saved');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error saving variance explanation:', error);
        this.showErrorSnackbar('Error occurred while saving. Please try again.');
        this.cdr.detectChanges();
      },
    );
  }

  removeVarianceExpln(): void {
    if (!this.varianceExpln?.EXPL_DESC) {
      this.showErrorSnackbar('Please select an explanation.');
      return;
    }

    this.confirmAction(
      'Remove Explanation',
      'Are you sure you want to remove this Explanation?',
      () => this.removeVarianceExplnConfirmed(),
    );
  }

  /** Remove variance explanation (action code: wr9xus). */
  private removeVarianceExplnConfirmed(): void {
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const ssoId = this.USER_SETTINGS?.user?.sso_id || this.globalParams?.sso_id;
    const jsonSettings = { sso_id: ssoId };

    this.service.removeVarExplanation(
      this.GENERAL_CONFIG.base_url,
      [this.buildRowKeysPayload()],
      jsonSettings,
      this.globalParams,
      this.filterParams,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this.showErrorSnackbar(response.errorMessage);
        } else {
          this.showSuccessSnackbar('Variance explanation has been removed');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error removing variance explanation:', error);
        this.showErrorSnackbar('Error occurred while removing. Please try again.');
        this.cdr.detectChanges();
      },
    );
  }

  private confirmAction(title: string, message: string, action: () => void): void {
    this.confirmModalConfig = { ...this.confirmModalConfig, title, message };
    this.pendingConfirmAction = action;
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmYes(): void {
    this.showConfirmDialog = false;
    this.pendingConfirmAction?.();
    this.pendingConfirmAction = null;
    this.cdr.detectChanges();
  }

  onConfirmNo(): void {
    this.showConfirmDialog = false;
    this.pendingConfirmAction = null;
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--orange', true, 4);
      return;
    }

    if (this.activeTab === 'setupExplanation') {
      this.saveNewExplanation();
    } else {
      this.saveVarianceExpln();
    }
  }

  reset(): void {
    if (this.activeTab === 'setupExplanation') {
      this.explainText = '';
    } else {
      this.explDataLoaded = false;
      this.loadFailed = false;
      this.getVarExplanation();
    }
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  private showErrorSnackbar(message: string): void {
    this.gtwSnackbar.openSnackBar(message, 'gtw-snackbar--red', true, 4);
  }

  private showSuccessSnackbar(message: string): void {
    this.gtwSnackbar.openSnackBar(message, 'gtw-snackbar--green', true, 4);
  }
}
